import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Activity,
  Bell,
  CalendarDays,
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  ClipboardList,
  Link as LinkIcon,
  Moon,
  Search,
  Shield,
  Trophy,
  UserPlus,
  Users,
} from "lucide-react";
import { cn } from "@v2/lib/utils";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { Input } from "@v2/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@v2/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@v2/components/ui/dialog";
import { supabaseV2 } from "../../lib/supabase";
import { EmptyState, SectionLoading } from "../../components/state";
import { MetricCard, PriorityTile, TeamCrest } from "../../components/primitives";
import { AreaChart } from "../../components/charts";
import { currentIntlLocale } from "../../lib/i18n";
import type { AppRole } from "../../types";
import { SupportAdminSection } from "./SupportAdminSection";
import { ResponseEventsAdminSection } from "./ResponseEventsAdminSection";
import { ActivityAdminSection } from "./ActivityAdminSection";

type DashboardStats = {
  users_total: number;
  users_founders: number;
  users_with_team: number;
  founders_active_30d: number;
  players_active_30d: number;
  teams_total: number;
  teams_active_30d: number;
  players_active: number;
  players_linked: number;
  guests_evaluating: number;
  sessions_30d: number;
  matches_30d: number;
  trainings_30d: number;
  response_rate_30d: number;
  push_users: number;
  ea_teams: number;
  pending_join_requests: number;
  pending_player_link_requests: number;
  unlinked_members: number;
  signups_30d: Array<{ day: string; count: number }>;
  top_teams: Array<{
    team_id: string;
    name: string;
    slug: string;
    players: number;
    sessions_30d: number;
    last_activity: string | null;
  }>;
  team_vitality: { alive: number; slowed: number; quiet: number; abandoned: number };
  dormant_teams: Array<{
    team_id: string;
    name: string;
    slug: string;
    players: number;
    last_activity: string | null;
  }>;
  recent_signups: Array<{
    user_id: string;
    email: string;
    full_name: string;
    created_at: string;
    team_name: string | null;
    team_id: string | null;
    via_invite: boolean;
  }>;
  generated_at: string;
};

// Etichetta "tempo fa" compatta: ora · Xm · Xh · Xg.
function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "ora";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}g`;
}

// Giorni intercorsi da una timestamptz; null se assente.
function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.floor(ms / 86_400_000));
}

export function AdminView({
  isSuperAdmin,
  currentTeamId,
  onOpenTeam,
}: {
  isSuperAdmin: boolean;
  currentTeamId: string | null;
  onOpenTeam: (teamId: string) => void;
}) {
  const { t } = useTranslation("settings");
  const intlLocale = currentIntlLocale();

  const statsQuery = useQuery({
    queryKey: ["admin-dashboard-stats"],
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("admin_dashboard_stats");
      if (error) throw error;
      return data as DashboardStats;
    },
    enabled: isSuperAdmin,
    staleTime: 60_000,
  });

  const teamsQuery = useQuery({
    queryKey: ["admin-teams"],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("teams")
        .select("id, name, slug, primary_color, secondary_color, created_at")
        .order("name");
      if (error) throw error;
      return (data || []) as Array<{
        id: string;
        name: string;
        slug: string;
        primary_color: string | null;
        secondary_color: string | null;
        created_at: string;
      }>;
    },
  });

  const appRolesQuery = useQuery({
    queryKey: ["admin-app-roles"],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("user_app_roles")
        .select("user_id, role, granted_at, granted_by")
        .order("granted_at", { ascending: false });
      if (error) throw error;
      return (data || []) as Array<{
        user_id: string;
        role: AppRole;
        granted_at: string;
        granted_by: string | null;
      }>;
    },
  });

  const tabs: AdminTab[] = isSuperAdmin
    ? ["dashboard", "activity", "users", "teams", "responses", "system"]
    : ["teams"];
  const [tab, setTab] = useState<AdminTab>(tabs[0]);
  const activeTab = tabs.includes(tab) ? tab : tabs[0];

  return (
    <div className="space-y-4 animate-fade-in">
      {tabs.length > 1 && (
        <div className="inline-flex rounded-2xl bg-background p-0.5 text-sm overflow-x-auto max-w-full">
          {tabs.map((tk) => (
            <button
              key={tk}
              onClick={() => setTab(tk)}
              className={cn(
                "px-3 py-1.5 rounded font-medium transition-colors whitespace-nowrap",
                activeTab === tk
                  ? "bg-card text-foreground "
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {t(`admin.tabs.${tk}`)}
            </button>
          ))}
        </div>
      )}

      {activeTab === "dashboard" && isSuperAdmin && (
        <DashboardSection
          stats={statsQuery.data}
          isLoading={statsQuery.isLoading}
          onOpenTeam={onOpenTeam}
          intlLocale={intlLocale}
        />
      )}

      {activeTab === "activity" && isSuperAdmin && (
        <ActivityAdminSection intlLocale={intlLocale} />
      )}

      {activeTab === "users" && isSuperAdmin && (
        <UsersTab intlLocale={intlLocale} teams={teamsQuery.data || []} />
      )}

      {activeTab === "teams" && (
        <TeamsCard
          teams={teamsQuery.data || []}
          isLoading={teamsQuery.isLoading}
          currentTeamId={currentTeamId}
          onOpenTeam={onOpenTeam}
          intlLocale={intlLocale}
        />
      )}

      {activeTab === "responses" && isSuperAdmin && (
        <ResponseEventsAdminSection teams={teamsQuery.data || []} intlLocale={intlLocale} />
      )}

      {activeTab === "system" && isSuperAdmin && (
        <div className="space-y-4">
          <RolesCard
            roles={appRolesQuery.data || []}
            isLoading={appRolesQuery.isLoading}
            isSuperAdmin={isSuperAdmin}
            intlLocale={intlLocale}
          />
          <SupportAdminSection />
        </div>
      )}
    </div>
  );
}

type AdminTab = "dashboard" | "activity" | "users" | "teams" | "responses" | "system";

function TeamsCard({
  teams,
  isLoading,
  currentTeamId,
  onOpenTeam,
  intlLocale,
}: {
  teams: Array<{
    id: string;
    name: string;
    slug: string;
    primary_color: string | null;
    secondary_color: string | null;
    created_at: string;
  }>;
  isLoading: boolean;
  currentTeamId: string | null;
  onOpenTeam: (teamId: string) => void;
  intlLocale: string;
}) {
  const { t } = useTranslation("settings");
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{t("admin.teamsTitle")}</CardTitle>
        <CardDescription>{t("admin.teamsDesc", { count: teams.length })}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-1">
        {teams.map((team) => {
          const isCurrent = team.id === currentTeamId;
          return (
            <div
              key={team.id}
              className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-2xl bg-background",
                isCurrent && "ring-1 ring-accent",
              )}
            >
              <TeamCrest team={team} size={28} />
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{team.name}</p>
                <p className="text-xs text-muted-foreground font-mono">
                  {team.slug} · {t("admin.createdAt", { date: new Date(team.created_at).toLocaleDateString(intlLocale) })}
                </p>
              </div>
              <Button
                size="sm"
                variant={isCurrent ? "secondary" : "outline"}
                className="h-7 text-xs"
                onClick={() => onOpenTeam(team.id)}
                disabled={isCurrent}
              >
                {isCurrent ? t("admin.active") : t("admin.open")}
              </Button>
            </div>
          );
        })}
        {isLoading && <SectionLoading />}
        {!isLoading && teams.length === 0 && <EmptyState text={t("admin.noTeams")} />}
      </CardContent>
    </Card>
  );
}

function RolesCard({
  roles,
  isLoading,
  isSuperAdmin,
  intlLocale,
}: {
  roles: Array<{ user_id: string; role: AppRole; granted_at: string; granted_by: string | null }>;
  isLoading: boolean;
  isSuperAdmin: boolean;
  intlLocale: string;
}) {
  const { t } = useTranslation("settings");
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{t("admin.rolesTitle")}</CardTitle>
        <CardDescription>
          {t("admin.rolesDescBase")}
          {isSuperAdmin ? "" : t("admin.rolesDescOnlySuper")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-1">
        {roles.map((r) => (
          <div
            key={`${r.user_id}-${r.role}`}
            className="flex items-center gap-3 px-3 py-2 border rounded-md bg-card text-sm"
          >
            <span className="font-mono text-xs text-muted-foreground flex-1">
              {r.user_id.slice(0, 8)}…
            </span>
            <span className="tag-mono">{r.role}</span>
            <span className="text-xs text-muted-foreground">
              {new Date(r.granted_at).toLocaleDateString(intlLocale)}
            </span>
          </div>
        ))}
        {isLoading && <SectionLoading />}
        {!isLoading && roles.length === 0 && <EmptyState text={t("admin.noRoles")} />}
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Tab Utenti: analisi (funnel, trend, cohort) + elenco filtrabile + drill-down.
// ──────────────────────────────────────────────────────────────────────────

type UserAnalytics = {
  funnel: { registered: number; confirmed: number; in_team: number; active_30d: number };
  signups_weekly: Array<{ week: string; total: number; via_invite: number; organic: number }>;
  cohorts: Array<{
    week: string;
    size: number;
    confirmed: number;
    in_team: number;
    active_recent: number;
  }>;
};

function UsersTab({
  intlLocale,
  teams,
}: {
  intlLocale: string;
  teams: Array<{ id: string; name: string }>;
}) {
  return (
    <div className="space-y-4">
      <UserAnalyticsPanel intlLocale={intlLocale} />
      <UsersSection intlLocale={intlLocale} teams={teams} />
    </div>
  );
}

function pctOf(part: number, whole: number): number {
  return whole > 0 ? Math.round((part / whole) * 100) : 0;
}

function UserAnalyticsPanel({ intlLocale }: { intlLocale: string }) {
  const { t } = useTranslation("settings");
  const analyticsQuery = useQuery({
    queryKey: ["admin-user-analytics"],
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("admin_user_analytics", {
        p_weeks: 12,
        p_cohort_weeks: 8,
      });
      if (error) throw error;
      return data as UserAnalytics;
    },
    staleTime: 60_000,
  });

  const data = analyticsQuery.data;

  if (analyticsQuery.isLoading || !data) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">{t("admin.users.analytics.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <SectionLoading />
        </CardContent>
      </Card>
    );
  }

  const f = data.funnel;
  const funnelSteps = [
    { key: "registered", value: f.registered, base: f.registered },
    { key: "confirmed", value: f.confirmed, base: f.registered },
    { key: "in_team", value: f.in_team, base: f.confirmed },
    { key: "active_30d", value: f.active_30d, base: f.in_team },
  ];
  const weekFmt = (iso: string) =>
    new Date(iso).toLocaleDateString(intlLocale, { day: "2-digit", month: "2-digit" });
  const weeks = data.signups_weekly;
  const labels: [string, string] =
    weeks.length > 0 ? [weekFmt(weeks[0].week), weekFmt(weeks[weeks.length - 1].week)] : ["", ""];
  const periodInvite = weeks.reduce((acc, w) => acc + w.via_invite, 0);
  const periodOrganic = weeks.reduce((acc, w) => acc + w.organic, 0);

  return (
    <div className="space-y-4">
      {/* Funnel di onboarding */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">{t("admin.users.funnel.title")}</CardTitle>
          <CardDescription>{t("admin.users.funnel.desc")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {funnelSteps.map((step, idx) => {
              const pctOfTotal = pctOf(step.value, f.registered);
              const conv = idx === 0 ? null : pctOf(step.value, step.base);
              return (
                <div key={step.key} className="rounded-2xl bg-background p-3">
                  <p className="text-2xl font-bold tabular-nums">{step.value}</p>
                  <p className="text-xs font-medium mt-0.5">
                    {t(`admin.users.funnel.step.${step.key}`)}
                  </p>
                  <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full bg-primary"
                      style={{ width: `${pctOfTotal}%` }}
                    />
                  </div>
                  {conv !== null && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {t("admin.users.funnel.conversion", { pct: conv })}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Andamento iscrizioni */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">{t("admin.users.trend.title")}</CardTitle>
          <CardDescription>
            {t("admin.users.trend.desc", { organic: periodOrganic, invite: periodInvite })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {weeks.length === 0 ? (
            <EmptyState text={t("admin.users.trend.empty")} />
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">
                  {t("admin.users.trend.organic")}
                </p>
                <AreaChart
                  values={weeks.map((w) => w.organic)}
                  labels={labels}
                  color="hsl(var(--accent))"
                  height={120}
                />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">
                  {t("admin.users.trend.viaInvite")}
                </p>
                <AreaChart
                  values={weeks.map((w) => w.via_invite)}
                  labels={labels}
                  color="hsl(var(--primary))"
                  height={120}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cohort di attivazione */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">{t("admin.users.cohort.title")}</CardTitle>
          <CardDescription>{t("admin.users.cohort.desc")}</CardDescription>
        </CardHeader>
        <CardContent>
          {data.cohorts.length === 0 ? (
            <EmptyState text={t("admin.users.cohort.empty")} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-muted-foreground">
                    <th className="text-left font-medium py-1.5 pr-3">
                      {t("admin.users.cohort.week")}
                    </th>
                    <th className="text-right font-medium py-1.5 px-2">
                      {t("admin.users.cohort.size")}
                    </th>
                    <th className="text-right font-medium py-1.5 px-2">
                      {t("admin.users.cohort.confirmed")}
                    </th>
                    <th className="text-right font-medium py-1.5 px-2">
                      {t("admin.users.cohort.inTeam")}
                    </th>
                    <th className="text-right font-medium py-1.5 pl-2">
                      {t("admin.users.cohort.activeRecent")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[...data.cohorts].reverse().map((c) => (
                    <tr key={c.week} className="border-t">
                      <td className="py-1.5 pr-3 font-medium tabular-nums">{weekFmt(c.week)}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums">{c.size}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums text-muted-foreground">
                        {c.confirmed}
                        <span className="text-xs ml-1">({pctOf(c.confirmed, c.size)}%)</span>
                      </td>
                      <td className="py-1.5 px-2 text-right tabular-nums text-muted-foreground">
                        {c.in_team}
                        <span className="text-xs ml-1">({pctOf(c.in_team, c.size)}%)</span>
                      </td>
                      <td className="py-1.5 pl-2 text-right tabular-nums text-muted-foreground">
                        {c.active_recent}
                        <span className="text-xs ml-1">({pctOf(c.active_recent, c.size)}%)</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Dashboard piattaforma — super_admin only. Tutto dietro a una sola RPC
// (admin_dashboard_stats), aggiornata ogni minuto via staleTime.
// ──────────────────────────────────────────────────────────────────────────

function DashboardSection({
  stats,
  isLoading,
  onOpenTeam,
  intlLocale,
}: {
  stats: DashboardStats | undefined;
  isLoading: boolean;
  onOpenTeam: (teamId: string) => void;
  intlLocale: string;
}) {
  const { t } = useTranslation("settings");

  if (isLoading || !stats) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">{t("admin.dashboard.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <SectionLoading />
        </CardContent>
      </Card>
    );
  }

  const responsePct = Math.round((stats.response_rate_30d || 0) * 100);
  const pushPct = stats.users_total > 0
    ? Math.round((stats.push_users / stats.users_total) * 100)
    : 0;
  const signupValues = stats.signups_30d.map((d) => d.count);
  const signupTotal = signupValues.reduce((acc, v) => acc + v, 0);
  const signupLabels = stats.signups_30d.length > 0
    ? [
        new Date(stats.signups_30d[0].day).toLocaleDateString(intlLocale, { day: "2-digit", month: "short" }),
        new Date(stats.signups_30d[stats.signups_30d.length - 1].day).toLocaleDateString(intlLocale, { day: "2-digit", month: "short" }),
      ]
    : [];
  const generatedAt = new Date(stats.generated_at).toLocaleString(intlLocale, {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });

  return (
    <div className="space-y-4">
      <section>
        <div className="flex items-baseline justify-between gap-3 mb-3 px-1">
          <div>
            <h2 className="text-xl font-semibold tracking-tight">{t("admin.dashboard.title")}</h2>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("admin.dashboard.updatedAt", { date: generatedAt })}
          </p>
        </div>

        {/* KPI hero */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard
            label={t("admin.dashboard.kpi.users")}
            value={stats.users_total}
            subtitle={t("admin.dashboard.kpi.usersBreakdown", {
              founders: stats.users_founders,
              players: Math.max(0, stats.users_with_team - stats.users_founders),
            })}
            icon={Users}
            gradient="gradient-card-teal"
          />
          <MetricCard
            label={t("admin.dashboard.kpi.teams")}
            value={stats.teams_total}
            subtitle={t("admin.dashboard.kpi.teamsActive", { count: stats.teams_active_30d })}
            icon={Shield}
            gradient="gradient-card-blue"
          />
          <MetricCard
            label={t("admin.dashboard.kpi.playersLinked")}
            value={stats.players_linked}
            subtitle={t("admin.dashboard.kpi.playersBreakdown", {
              total: stats.players_active,
              guests: stats.guests_evaluating,
            })}
            icon={UserPlus}
            gradient="gradient-card-purple"
          />
          <MetricCard
            label={t("admin.dashboard.kpi.sessions30d")}
            value={stats.sessions_30d}
            subtitle={t("admin.dashboard.kpi.sessionsBreakdown", {
              trainings: stats.trainings_30d,
              matches: stats.matches_30d,
            })}
            icon={Activity}
            gradient="gradient-card-orange"
          />
        </div>
      </section>

      {/* Trend iscrizioni */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t("admin.dashboard.trend.title")}</CardTitle>
          <CardDescription>
            {signupTotal > 0
              ? t("admin.dashboard.trend.desc", { count: signupTotal })
              : t("admin.dashboard.trend.empty")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {signupTotal > 0 ? (
            <AreaChart values={signupValues} labels={signupLabels} color="hsl(var(--accent))" height={160} />
          ) : (
            <EmptyState text={t("admin.dashboard.trend.empty")} />
          )}
        </CardContent>
      </Card>

      {/* Ultimi iscritti con la loro squadra (se ne hanno una). Utile per
          accorgersi di account orfani (es. signup generico senza scelta team). */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t("admin.dashboard.recentSignups.title")}</CardTitle>
          <CardDescription>{t("admin.dashboard.recentSignups.desc")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-1.5">
          {stats.recent_signups.length === 0 ? (
            <EmptyState text={t("admin.dashboard.recentSignups.empty")} />
          ) : (
            stats.recent_signups.map((row) => (
              <div
                key={row.user_id}
                className="flex items-center gap-3 px-3 py-2 rounded-2xl bg-background text-sm"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{row.full_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{row.email}</p>
                </div>
                <div className="shrink-0 min-w-0 max-w-[40%] text-right">
                  {row.team_name ? (
                    <button
                      type="button"
                      onClick={() => row.team_id && onOpenTeam(row.team_id)}
                      disabled={!row.team_id}
                      className="text-xs font-medium truncate hover:text-primary disabled:cursor-default disabled:hover:text-foreground inline-flex items-center gap-1.5"
                      title={row.team_id ? t("admin.dashboard.recentSignups.openTeam") : undefined}
                    >
                      <span className="truncate">{row.team_name}</span>
                      {row.via_invite && (
                        <span className="text-[11px] bg-sky-500/15 text-sky-700 font-semibold px-1 py-0.5 rounded shrink-0">
                          {t("admin.dashboard.recentSignups.viaInvite")}
                        </span>
                      )}
                    </button>
                  ) : (
                    <span className="text-xs text-amber-700 bg-amber-500/10 px-1.5 py-0.5 rounded">
                      {t("admin.dashboard.recentSignups.noTeam")}
                    </span>
                  )}
                </div>
                <span className="text-xs text-muted-foreground tabular-nums w-9 text-right">
                  {timeAgo(row.created_at)}
                </span>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Engagement + coda operativa */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t("admin.dashboard.engagement.title")}</CardTitle>
            <CardDescription>{t("admin.dashboard.engagement.desc")}</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <MetricCard
              label={t("admin.dashboard.engagement.responseRate")}
              value={`${responsePct}%`}
              icon={ClipboardList}
            />
            <MetricCard
              label={t("admin.dashboard.engagement.pushAdoption")}
              value={`${pushPct}%`}
              subtitle={t("admin.dashboard.engagement.pushSub", { count: stats.push_users })}
              icon={Bell}
            />
            <MetricCard
              label={t("admin.dashboard.engagement.eaConnected")}
              value={`${stats.ea_teams}/${stats.teams_total}`}
              icon={LinkIcon}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t("admin.dashboard.queue.title")}</CardTitle>
            <CardDescription>{t("admin.dashboard.queue.desc")}</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-3 gap-3">
            <PriorityTile
              label={t("admin.dashboard.queue.joinRequests")}
              value={stats.pending_join_requests}
              accent={stats.pending_join_requests > 0 ? "text-amber-600" : "text-muted-foreground"}
            />
            <PriorityTile
              label={t("admin.dashboard.queue.playerLinks")}
              value={stats.pending_player_link_requests}
              accent={stats.pending_player_link_requests > 0 ? "text-sky-600" : "text-muted-foreground"}
            />
            <PriorityTile
              label={t("admin.dashboard.queue.unlinkedMembers")}
              value={stats.unlinked_members}
              accent={stats.unlinked_members > 0 ? "text-rose-600" : "text-muted-foreground"}
            />
          </CardContent>
        </Card>
      </div>

      {/* Utilizzo per ruolo: chi entra davvero nell'app, founder vs player. */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t("admin.dashboard.usage.title")}</CardTitle>
          <CardDescription>{t("admin.dashboard.usage.desc")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-1.5">
          {(() => {
            const playersTotal = Math.max(0, stats.users_with_team - stats.users_founders);
            const rows = [
              {
                key: "founders",
                label: t("admin.dashboard.usage.founders"),
                total: stats.users_founders,
                active: stats.founders_active_30d,
              },
              {
                key: "players",
                label: t("admin.dashboard.usage.players"),
                total: playersTotal,
                active: stats.players_active_30d,
              },
            ];
            return rows.map((r) => {
              const pct = r.total > 0 ? Math.round((r.active / r.total) * 100) : 0;
              return (
                <div
                  key={r.key}
                  className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-4 px-3 py-2 rounded-2xl bg-background text-sm"
                >
                  <span className="font-semibold">{r.label}</span>
                  <span className="text-muted-foreground tabular-nums">
                    {t("admin.dashboard.usage.total", { count: r.total })}
                  </span>
                  <span className="tabular-nums">
                    {t("admin.dashboard.usage.active", { count: r.active })}
                  </span>
                  <span
                    className={
                      "tabular-nums text-xs font-mono px-2 py-0.5 rounded " +
                      (pct >= 60 ? "bg-emerald-500/15 text-emerald-700"
                        : pct >= 30 ? "bg-amber-500/15 text-amber-700"
                        : "bg-rose-500/15 text-rose-700")
                    }
                  >
                    {pct}%
                  </span>
                </div>
              );
            });
          })()}
        </CardContent>
      </Card>

      {/* Vitalità + squadre dormienti — derivate da segnali esistenti
          (login, risposte sessioni, creazione sessioni), niente tracking nuovo. */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t("admin.dashboard.vitality.title")}</CardTitle>
            <CardDescription>{t("admin.dashboard.vitality.desc")}</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <PriorityTile
              label={t("admin.dashboard.vitality.alive")}
              value={stats.team_vitality.alive}
              accent="text-emerald-600"
            />
            <PriorityTile
              label={t("admin.dashboard.vitality.slowed")}
              value={stats.team_vitality.slowed}
              accent="text-amber-600"
            />
            <PriorityTile
              label={t("admin.dashboard.vitality.quiet")}
              value={stats.team_vitality.quiet}
              accent="text-orange-600"
            />
            <PriorityTile
              label={t("admin.dashboard.vitality.abandoned")}
              value={stats.team_vitality.abandoned}
              accent="text-slate-500"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Moon className="h-4 w-4 text-slate-500" />
              {t("admin.dashboard.dormant.title")}
            </CardTitle>
            <CardDescription>{t("admin.dashboard.dormant.desc")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {stats.dormant_teams.length === 0 ? (
              <EmptyState text={t("admin.dashboard.dormant.empty")} />
            ) : (
              stats.dormant_teams.map((row) => {
                const days = daysSince(row.last_activity);
                return (
                  <button
                    key={row.team_id}
                    onClick={() => onOpenTeam(row.team_id)}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-2xl bg-background hover:bg-muted/40 transition-colors text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate text-sm">{row.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {t("admin.dashboard.topTeams.players", { count: row.players })}
                        {" · "}
                        {days === null
                          ? t("admin.dashboard.dormant.never")
                          : t("admin.dashboard.dormant.daysAgo", { count: days })}
                      </p>
                    </div>
                  </button>
                );
              })
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top squadre per attività */}
      <Card>
        <CardHeader className="pb-3 flex flex-row items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="h-4 w-4 text-amber-500" />
              {t("admin.dashboard.topTeams.title")}
            </CardTitle>
            <CardDescription>{t("admin.dashboard.topTeams.desc")}</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-1.5">
          {stats.top_teams.map((row, idx) => (
            <button
              key={row.team_id}
              onClick={() => onOpenTeam(row.team_id)}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-2xl bg-background hover:bg-muted/40 transition-colors text-left"
            >
              <span className="w-5 text-center text-xs font-mono text-muted-foreground">{idx + 1}</span>
              <CalendarDays className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate text-sm">{row.name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {t("admin.dashboard.topTeams.players", { count: row.players })}
                  {" · "}
                  {row.last_activity
                    ? t("admin.dashboard.topTeams.lastActivity", {
                        date: new Date(row.last_activity).toLocaleDateString(intlLocale, { day: "2-digit", month: "short" }),
                      })
                    : t("admin.dashboard.topTeams.noActivity")}
                </p>
              </div>
              <span className="text-sm font-semibold tabular-nums shrink-0">
                {row.sessions_30d}
                <span className="text-xs text-muted-foreground ml-1">
                  {t("admin.dashboard.topTeams.sessionsUnit")}
                </span>
              </span>
            </button>
          ))}
          {stats.top_teams.length === 0 && (
            <EmptyState text={t("admin.dashboard.topTeams.empty")} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Listato utenti — super_admin only. Tabella filtrabile alimentata dalla RPC
// admin_list_users (espone auth.users: conferma email, ultimo accesso).
// ──────────────────────────────────────────────────────────────────────────

type AdminUserRow = {
  user_id: string;
  email: string;
  full_name: string;
  created_at: string;
  email_confirmed_at: string | null;
  last_sign_in_at: string | null;
  email_confirmed: boolean;
  team_name: string | null;
  team_role: string | null;
  has_team: boolean;
  via_invite: boolean;
  invite_team_name: string | null;
  has_player: boolean;
};

type AdminUsersResult = {
  users: AdminUserRow[];
  total: number;
  limit: number;
  offset: number;
};

type UsersFilter = "all" | "unconfirmed" | "no_team" | "inactive";
type UsersSortKey = "full_name" | "email" | "created_at" | "last_sign_in_at";

const USERS_PAGE_SIZE = 25;
const USERS_FILTERS: UsersFilter[] = ["all", "unconfirmed", "no_team", "inactive"];

function SortHeader({
  label,
  sortKey,
  activeSort,
  dir,
  onSort,
  align = "left",
}: {
  label: string;
  sortKey: UsersSortKey;
  activeSort: UsersSortKey;
  dir: "asc" | "desc";
  onSort: (key: UsersSortKey) => void;
  align?: "left" | "right";
}) {
  const active = activeSort === sortKey;
  return (
    <th className={cn("font-medium py-1.5 px-2", align === "right" ? "text-right" : "text-left")}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          "inline-flex items-center gap-1 hover:text-foreground transition-colors",
          align === "right" && "flex-row-reverse",
          active && "text-foreground",
        )}
      >
        {label}
        {active ? (
          dir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronsUpDown className="h-3 w-3 opacity-40" />
        )}
      </button>
    </th>
  );
}

function UsersSection({
  intlLocale,
  teams,
}: {
  intlLocale: string;
  teams: Array<{ id: string; name: string }>;
}) {
  const { t } = useTranslation("settings");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<UsersFilter>("all");
  const [teamId, setTeamId] = useState("");
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<UsersSortKey>("created_at");
  const [dir, setDir] = useState<"asc" | "desc">("desc");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  useEffect(() => {
    const id = window.setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  // Reset pagina quando cambiano i criteri.
  useEffect(() => {
    setPage(0);
  }, [search, filter, teamId, sort, dir]);

  const usersQuery = useQuery({
    queryKey: ["admin-users", search, filter, teamId, sort, dir, page],
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("admin_list_users", {
        p_search: search || null,
        p_filter: filter,
        p_team_id: teamId || null,
        p_sort: sort,
        p_dir: dir,
        p_inactive_days: 30,
        p_limit: USERS_PAGE_SIZE,
        p_offset: page * USERS_PAGE_SIZE,
      });
      if (error) throw error;
      return data as AdminUsersResult;
    },
    staleTime: 30_000,
  });

  const toggleSort = (key: UsersSortKey) => {
    if (sort === key) {
      setDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSort(key);
      setDir(key === "full_name" || key === "email" ? "asc" : "desc");
    }
  };

  const sortedTeams = [...teams].sort((a, b) => a.name.localeCompare(b.name));

  const result = usersQuery.data;
  const rows = result?.users || [];
  const total = result?.total || 0;
  const from = total === 0 ? 0 : page * USERS_PAGE_SIZE + 1;
  const to = Math.min(total, (page + 1) * USERS_PAGE_SIZE);
  const hasPrev = page > 0;
  const hasNext = (page + 1) * USERS_PAGE_SIZE < total;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          {t("admin.users.title")}
        </CardTitle>
        <CardDescription>{t("admin.users.desc", { count: total })}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-2">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t("admin.users.searchPlaceholder")}
                className="pl-8 h-9"
              />
            </div>
            <Select
              value={teamId || "__all__"}
              onValueChange={(v) => setTeamId(v === "__all__" ? "" : v)}
            >
              <SelectTrigger className="h-9 sm:w-[220px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t("admin.users.allTeams")}</SelectItem>
                {sortedTeams.map((tm) => (
                  <SelectItem key={tm.id} value={tm.id}>
                    {tm.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {USERS_FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs font-medium transition-smooth",
                  filter === f
                    ? "border-transparent bg-primary text-primary-foreground"
                    : "bg-card hover:bg-muted/60 text-foreground/80",
                )}
              >
                {t(`admin.users.filters.${f}`)}
              </button>
            ))}
          </div>
        </div>

        {usersQuery.isLoading && <SectionLoading />}
        {!usersQuery.isLoading && rows.length === 0 && (
          <EmptyState text={t("admin.users.empty")} />
        )}

        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[680px]">
              <thead>
                <tr className="text-xs text-muted-foreground border-b">
                  <SortHeader
                    label={t("admin.users.col.name")}
                    sortKey="full_name"
                    activeSort={sort}
                    dir={dir}
                    onSort={toggleSort}
                  />
                  <SortHeader
                    label={t("admin.users.col.email")}
                    sortKey="email"
                    activeSort={sort}
                    dir={dir}
                    onSort={toggleSort}
                  />
                  <th className="text-left font-medium py-1.5 px-2">{t("admin.users.col.status")}</th>
                  <th className="text-left font-medium py-1.5 px-2">{t("admin.users.col.team")}</th>
                  <SortHeader
                    label={t("admin.users.col.registered")}
                    sortKey="created_at"
                    activeSort={sort}
                    dir={dir}
                    onSort={toggleSort}
                    align="right"
                  />
                  <SortHeader
                    label={t("admin.users.col.lastSignIn")}
                    sortKey="last_sign_in_at"
                    activeSort={sort}
                    dir={dir}
                    onSort={toggleSort}
                    align="right"
                  />
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr
                    key={u.user_id}
                    onClick={() => setSelectedUserId(u.user_id)}
                    className="border-b last:border-0 cursor-pointer hover:bg-muted/40 transition-colors"
                  >
                    <td className="py-2 px-2 max-w-[200px]">
                      <p className="font-medium truncate">{u.full_name}</p>
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {u.via_invite && (
                          <span className="rounded bg-muted text-muted-foreground px-1 py-0.5 text-[11px] font-medium">
                            {t("admin.users.badge.viaInvite")}
                          </span>
                        )}
                        {u.has_player && (
                          <span className="rounded bg-muted text-muted-foreground px-1 py-0.5 text-[11px] font-medium">
                            {t("admin.users.badge.hasPlayer")}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-2 max-w-[200px]">
                      <span className="font-mono text-xs text-muted-foreground truncate block">
                        {u.email}
                      </span>
                    </td>
                    <td className="py-2 px-2 whitespace-nowrap">
                      {u.email_confirmed ? (
                        <span className="text-xs text-emerald-600">
                          {t("admin.users.confirmedShort")}
                        </span>
                      ) : (
                        <span className="text-xs text-amber-600 font-medium">
                          {t("admin.users.unconfirmedShort")}
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-2 max-w-[180px]">
                      {u.has_team ? (
                        <span className="text-[12px] truncate block">
                          {u.team_name}
                          <span className="text-muted-foreground">
                            {" · "}
                            {t(`admin.users.role.${u.team_role || "player"}`, {
                              defaultValue: u.team_role || "",
                            })}
                          </span>
                        </span>
                      ) : (
                        <span className="text-xs text-sky-600">
                          {t("admin.users.badge.noTeam")}
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-right text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                      {new Date(u.created_at).toLocaleDateString(intlLocale)}
                    </td>
                    <td className="py-2 px-2 text-right text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                      {u.last_sign_in_at
                        ? t("admin.users.lastSignIn", { when: timeAgo(u.last_sign_in_at) })
                        : t("admin.users.neverSignedIn")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > USERS_PAGE_SIZE && (
          <div className="flex items-center justify-between pt-1">
            <span className="text-xs text-muted-foreground tabular-nums">
              {t("admin.users.pageInfo", { from, to, total })}
            </span>
            <div className="flex gap-1.5">
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                disabled={!hasPrev}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                {t("admin.users.prev")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                disabled={!hasNext}
                onClick={() => setPage((p) => p + 1)}
              >
                {t("admin.users.next")}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
      <UserDetailDialog
        userId={selectedUserId}
        onClose={() => setSelectedUserId(null)}
        intlLocale={intlLocale}
      />
    </Card>
  );
}


// ──────────────────────────────────────────────────────────────────────────
// Drill-down singolo utente: timeline completa via RPC admin_user_detail.
// ──────────────────────────────────────────────────────────────────────────

type UserDetail = {
  user: {
    id: string;
    email: string;
    full_name: string;
    created_at: string;
    email_confirmed_at: string | null;
    last_sign_in_at: string | null;
    via_invite: boolean;
    invite_team_name: string | null;
  };
  memberships: Array<{
    team_id: string;
    team_name: string;
    role: string;
    status: string;
    created_at: string;
  }>;
  join_requests: Array<{
    team_name: string;
    status: string;
    requested_at: string;
    decided_at: string | null;
  }>;
  players: Array<{
    team_name: string;
    display_name: string;
    status: string;
    is_guest: boolean;
  }>;
  responses: {
    total: number;
    present: number;
    late: number;
    absent: number;
    no_response: number;
  };
  notifications: Array<{
    type: string;
    title: string;
    created_at: string;
    seen: boolean;
  }>;
};

function DetailBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-1.5">
      <h3 className="text-xs text-muted-foreground font-semibold">
        {title}
      </h3>
      {children}
    </section>
  );
}

function UserDetailDialog({
  userId,
  onClose,
  intlLocale,
}: {
  userId: string | null;
  onClose: () => void;
  intlLocale: string;
}) {
  const { t } = useTranslation("settings");
  const detailQuery = useQuery({
    queryKey: ["admin-user-detail", userId],
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("admin_user_detail", {
        p_user_id: userId,
      });
      if (error) throw error;
      return data as UserDetail;
    },
    enabled: !!userId,
  });

  const d = detailQuery.data;
  const fmtDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString(intlLocale, { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <Dialog open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{d ? d.user.full_name : t("admin.users.detail.title")}</DialogTitle>
          {d && <DialogDescription className="font-mono text-xs">{d.user.email}</DialogDescription>}
        </DialogHeader>

        {detailQuery.isLoading && <SectionLoading />}
        {detailQuery.isError && <EmptyState text={t("admin.users.detail.error")} />}

        {d && (
          <div className="space-y-4 text-sm">
            <DetailBlock title={t("admin.users.detail.account")}>
              <div className="rounded-2xl bg-background px-3 py-2 space-y-1 text-[12.5px]">
                <p className="flex justify-between gap-3">
                  <span className="text-muted-foreground">{t("admin.users.detail.registered")}</span>
                  <span className="tabular-nums">{fmtDate(d.user.created_at)}</span>
                </p>
                <p className="flex justify-between gap-3">
                  <span className="text-muted-foreground">{t("admin.users.detail.emailStatus")}</span>
                  <span>
                    {d.user.email_confirmed_at
                      ? t("admin.users.detail.confirmed")
                      : t("admin.users.badge.unconfirmed")}
                  </span>
                </p>
                <p className="flex justify-between gap-3">
                  <span className="text-muted-foreground">{t("admin.users.detail.lastSignIn")}</span>
                  <span className="tabular-nums">
                    {d.user.last_sign_in_at ? fmtDate(d.user.last_sign_in_at) : t("admin.users.neverSignedIn")}
                  </span>
                </p>
                {d.user.via_invite && (
                  <p className="flex justify-between gap-3">
                    <span className="text-muted-foreground">{t("admin.users.detail.origin")}</span>
                    <span>
                      {d.user.invite_team_name
                        ? t("admin.users.badge.viaInviteFrom", { team: d.user.invite_team_name })
                        : t("admin.users.badge.viaInvite")}
                    </span>
                  </p>
                )}
              </div>
            </DetailBlock>

            <DetailBlock title={t("admin.users.detail.memberships")}>
              {d.memberships.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t("admin.users.detail.none")}</p>
              ) : (
                d.memberships.map((m) => (
                  <div
                    key={`${m.team_id}-${m.role}`}
                    className="flex items-center justify-between gap-3 rounded-2xl bg-background px-3 py-1.5 text-[12.5px]"
                  >
                    <span className="font-medium truncate">{m.team_name}</span>
                    <span className="text-muted-foreground shrink-0">
                      {t(`admin.users.role.${m.role}`, { defaultValue: m.role })} · {m.status}
                    </span>
                  </div>
                ))
              )}
            </DetailBlock>

            <DetailBlock title={t("admin.users.detail.players")}>
              {d.players.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t("admin.users.detail.none")}</p>
              ) : (
                d.players.map((p, i) => (
                  <div
                    key={`${p.team_name}-${i}`}
                    className="flex items-center justify-between gap-3 rounded-2xl bg-background px-3 py-1.5 text-[12.5px]"
                  >
                    <span className="font-medium truncate">{p.display_name}</span>
                    <span className="text-muted-foreground shrink-0">
                      {p.team_name} · {p.is_guest ? t("admin.users.detail.guest") : p.status}
                    </span>
                  </div>
                ))
              )}
            </DetailBlock>

            <DetailBlock title={t("admin.users.detail.responses")}>
              <div className="grid grid-cols-4 gap-1.5">
                {(["present", "late", "absent", "no_response"] as const).map((k) => (
                  <div key={k} className="rounded-2xl bg-background px-2 py-1.5 text-center">
                    <p className="text-base font-bold tabular-nums">{d.responses[k]}</p>
                    <p className="text-xs text-muted-foreground">
                      {t(`admin.users.detail.resp.${k}`)}
                    </p>
                  </div>
                ))}
              </div>
            </DetailBlock>

            {d.join_requests.length > 0 && (
              <DetailBlock title={t("admin.users.detail.joinRequests")}>
                {d.join_requests.map((j, i) => (
                  <div
                    key={`${j.team_name}-${i}`}
                    className="flex items-center justify-between gap-3 rounded-2xl bg-background px-3 py-1.5 text-[12.5px]"
                  >
                    <span className="font-medium truncate">{j.team_name}</span>
                    <span className="text-muted-foreground shrink-0">
                      {j.status} · {fmtDate(j.requested_at)}
                    </span>
                  </div>
                ))}
              </DetailBlock>
            )}

            {d.notifications.length > 0 && (
              <DetailBlock title={t("admin.users.detail.notifications")}>
                {d.notifications.map((n, i) => (
                  <div
                    key={`${n.type}-${i}`}
                    className="flex items-center justify-between gap-3 rounded-2xl bg-background px-3 py-1.5 text-[12.5px]"
                  >
                    <span className="truncate">{n.title}</span>
                    <span className="text-muted-foreground shrink-0 tabular-nums">
                      {fmtDate(n.created_at)}
                    </span>
                  </div>
                ))}
              </DetailBlock>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
